import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateCategory, getListCategoriesQueryKey } from "@workspace/api-client-react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Loader2, Wand2, ImagePlus, ShoppingBag, Package2, Wrench, Wheat, X, Plus, Check, ToggleLeft, AlertCircle, AlertTriangle, Printer, ChevronsUpDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { BarcodePrinter } from "@/components/barcode-printer";
import { useAuth } from "@/context/auth";
import JsBarcode from "jsbarcode";
import { detectBarcodeFormat, getQuietZoneMargins } from "@/lib/barcode-utils";

export const productSchema = z.object({
  name: z.string().min(1, "Product name is required"),
  sku: z.string().optional(),
  description: z.string().optional(),
  categoryId: z.string().min(1, "Category is required").refine(v => v !== "none", "Category is required"),
  brand: z.string().optional(),
  price: z.coerce.number().min(0, "Selling price is required"),
  costPrice: z.coerce.number().min(0),
  stock: z.coerce.number().min(0),
  reorderLevel: z.coerce.number().int().min(0),
  unit: z.string().optional(),
  isService: z.boolean().optional(),
  vatApplicable: z.boolean().optional(),
  expiryDate: z.string().nullable().optional(),
});

export type ProductFormValues = z.infer<typeof productSchema>;

export const PRODUCT_FORM_DEFAULTS: ProductFormValues = {
  name: "",
  sku: "",
  description: "",
  categoryId: "",
  brand: "",
  price: 0,
  costPrice: 0,
  stock: 0,
  reorderLevel: 5,
  unit: "piece",
  isService: false,
  vatApplicable: false,
  expiryDate: null,
};

const PRODUCT_TYPES = [
  { value: "single", label: "Single Item", desc: "Standard item sold as-is", icon: ShoppingBag },
  { value: "variable", label: "Variable Item", desc: "Has sizes/colors/variants", icon: Package2 },
  { value: "service", label: "Service", desc: "Non-physical / labour / digital", icon: Wrench },
  { value: "material", label: "Raw Material", desc: "Ingredient or production input", icon: Wheat },
];

const UNITS = ["piece", "pair", "set", "pack", "box", "carton", "bag", "bottle", "litre", "kg", "gram", "metre", "yard"];

function SectionHeader({ number, title }: { number: number; title: string }) {
  return (
    <div className="flex items-center gap-3 pb-2 border-b">
      <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
        {number}
      </div>
      <h3 className="font-semibold text-base">{title}</h3>
    </div>
  );
}

function FieldHint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground mt-1">{children}</p>;
}

function ProductBarcodePreview({ value, generated }: { value: string; generated: boolean }) {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const [renderError, setRenderError] = useState(false);

  useEffect(() => {
    if (!barcodeRef.current) return;
    try {
      const format = detectBarcodeFormat(value);
      JsBarcode(barcodeRef.current, value, {
        format,
        width: 2.4,
        height: 96,
        displayValue: false,
        marginTop: 2,
        marginBottom: 2,
        lineColor: "#111111",
        ...getQuietZoneMargins(format, 2.4),
      });
      setRenderError(false);
    } catch {
      barcodeRef.current.innerHTML = "";
      setRenderError(true);
    }
  }, [value]);

  return (
    <div className="mt-3 w-full rounded-lg border-2 border-primary/20 bg-white p-4" aria-live="polite">
      <p className="mb-2 flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700">
        <Check className="h-4 w-4" aria-hidden="true" />
        {generated ? "Barcode generated" : "Barcode preview"}
      </p>
      <p className="mb-3 text-center text-xs text-muted-foreground">Ready to scan and print</p>
      <div className="flex min-h-28 w-full items-center justify-center overflow-x-auto rounded-md bg-white py-2">
        <svg ref={barcodeRef} role="img" aria-label={`Barcode ${value}`} className="block h-auto max-w-full" />
      </div>
      <p className="mt-3 break-all text-center font-mono text-sm font-semibold text-foreground">{value}</p>
      {renderError && <p className="mt-1 text-center text-xs text-destructive">This code could not be displayed as a barcode.</p>}
    </div>
  );
}

interface ProductFormProps {
  defaultValues?: ProductFormValues;
  categories?: { id: number; name: string }[];
  categoriesLoading?: boolean;
  brands?: string[];
  productId?: number;
  onSubmit: (data: ProductFormValues, addAnother: boolean, printAfterSave?: boolean) => void;
  onCancel: () => void;
  onPrintBarcode?: (product: { id: number; name: string; sku: string; price: number }) => void;
  isPending: boolean;
  isEdit?: boolean;
}

export function ProductForm({ defaultValues, categories, categoriesLoading = false, brands = [], productId, onSubmit, onCancel, onPrintBarcode, isPending, isEdit = false }: ProductFormProps) {
  const queryClient = useQueryClient();
  const createCategory = useCreateCategory();
  const { toast } = useToast();
  const { store } = useAuth();

  // SKU duplicate detection
  type SkuConflict = { id: number; name: string; sku: string; variantLabel?: string } | null;
  const [skuConflict, setSkuConflict] = useState<SkuConflict>(null);
  const [skuChecking, setSkuChecking] = useState(false);
  const [skuGenerating, setSkuGenerating] = useState(false);
  const [generatedSku, setGeneratedSku] = useState("");
  const [variantSkusGenerating, setVariantSkusGenerating] = useState(false);
  const [barcodePrinterOpen, setBarcodePrinterOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const categoryPickerRef = useRef<HTMLDivElement>(null);
  const skuDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productSubmitLockRef = useRef(false);
  const saveMutationStartedRef = useRef(false);

  const closeCategoryPicker = useCallback(() => {
    setCategoryOpen(false);
    setCategorySearch("");
  }, []);

  useEffect(() => {
    if (!categoryOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (!categoryPickerRef.current?.contains(event.target as Node)) {
        closeCategoryPicker();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeCategoryPicker();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [categoryOpen, closeCategoryPicker]);

  useEffect(() => {
    if (isPending) {
      saveMutationStartedRef.current = true;
    } else if (saveMutationStartedRef.current) {
      saveMutationStartedRef.current = false;
      productSubmitLockRef.current = false;
    }
  }, [isPending]);

  const checkSku = useCallback(async (sku: string): Promise<SkuConflict> => {
    const trimmed = sku.trim();
    if (!trimmed) { setSkuConflict(null); return null; }
    setSkuChecking(true);
    try {
      const params = new URLSearchParams({ sku: trimmed });
      if (productId) params.set("excludeId", String(productId));
      const res = await fetch(`/api/products/check-sku?${params}`);
      if (!res.ok) return null;
      const data = await res.json();
      const conflict = data.exists ? data.product : null;
      setSkuConflict(conflict);
      return conflict;
    } catch { /* ignore network errors */ }
    finally { setSkuChecking(false); }
    return null;
  }, [productId]);

  const generateSKU = async (): Promise<string | null> => {
    setSkuGenerating(true);
    try {
      const res = await fetch("/api/products/generate-barcode", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not generate barcode");
      form.setValue("sku", data.barcode, { shouldDirty: true, shouldValidate: true });
      setSkuConflict(null);
      setGeneratedSku(String(data.barcode));
      return String(data.barcode);
    } catch (error) {
      toast({
        title: "Barcode not generated",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setSkuGenerating(false);
    }
  };

  const [productType, setProductType] = useState(() =>
    defaultValues?.isService ? "service" : (defaultValues as any)?.hasVariants ? "variable" : "single"
  );
  const isServiceType = productType === "service";
  const isVariableType = productType === "variable";
  const isMaterialType = productType === "material";

  type VariantRow = {
    id: string;
    label: string;
    color: string;
    size: string;
    sku: string;
    price: string;
    stock: string;
    attribute1Label?: string;
    attribute1Value?: string;
    attribute2Label?: string;
    attribute2Value?: string;
  };
  const parseVariants = (raw?: string | null): VariantRow[] => {
    try {
      return JSON.parse(raw || "[]").map((v: any) => {
        const attributes = [
          [String(v.attribute1Label ?? ""), String(v.attribute1Value ?? "")],
          [String(v.attribute2Label ?? ""), String(v.attribute2Value ?? "")],
        ];
        const namedValue = (name: string) => attributes.find(([label]) => label.trim().toLowerCase() === name)?.[1];
        const [legacyColor = "", ...legacySize] = String(v.label ?? "").split("/");
        return {
          ...v,
          color: namedValue("color")?.trim() || legacyColor.trim(),
          size: namedValue("size")?.trim() || legacySize.join("/").trim(),
          price: String(v.price ?? ""),
          stock: String(v.stock ?? ""),
        };
      });
    } catch {
      return [];
    }
  };
  const [variants, setVariants] = useState<VariantRow[]>(() => parseVariants((defaultValues as any)?.variants));
  const addVariantRow = () => setVariants(prev => [...prev, { id: crypto.randomUUID(), label: "", color: "", size: "", sku: "", price: "", stock: "" }]);
  const removeVariantRow = (id: string) => setVariants(prev => prev.filter(v => v.id !== id));
  const updateVariantRow = (id: string, key: keyof VariantRow, val: string) => setVariants(prev => prev.map(v => v.id === id ? { ...v, [key]: val } : v));
  const [addAnother, setAddAnother] = useState(false);
  const [printAfterSave, setPrintAfterSave] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [savingCategory, setSavingCategory] = useState(false);

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(productSchema),
    defaultValues: defaultValues ?? PRODUCT_FORM_DEFAULTS,
  });

  useEffect(() => {
    if (defaultValues) form.reset(defaultValues);
  }, [defaultValues]);

  useEffect(() => {
    if (!isEdit && !form.getValues("categoryId") && categories?.length) {
      const generalCategory = categories.find(category => /^(general|other|uncategorized)$/i.test(category.name.trim()));
      if (generalCategory || categories.length === 1) {
        form.setValue("categoryId", String((generalCategory ?? categories[0]).id), { shouldValidate: true });
      }
    }
  }, [categories, form, isEdit]);

  const watchedValues = useWatch({ control: form.control });
  const costPrice = Number(watchedValues.costPrice) || 0;
  const sellingPrice = Number(watchedValues.price) || 0;

  const [margin, setMargin] = useState<string>("");

  const computedMargin = useMemo(() => {
    if (!costPrice || !sellingPrice) return null;
    return (((sellingPrice - costPrice) / costPrice) * 100).toFixed(1);
  }, [costPrice, sellingPrice]);

  const applyMargin = () => {
    const m = parseFloat(margin);
    if (!isNaN(m) && costPrice > 0) {
      const newPrice = costPrice * (1 + m / 100);
      form.setValue("price", parseFloat(newPrice.toFixed(2)));
    }
  };

  const requiredFields: (keyof ProductFormValues)[] = ["name", "price", "categoryId"];
  const filledRequired = requiredFields.filter(f => {
    const v = watchedValues[f];
    return v !== undefined && v !== null && v !== "" && v !== "none" && Number(v) >= 0 && !(f === "price" && Number(v) === 0);
  }).length;
  const optionalFields: (keyof ProductFormValues)[] = ["costPrice", "stock", "reorderLevel", "unit", "description"];
  const filledOptional = optionalFields.filter(f => {
    const v = watchedValues[f];
    return v !== undefined && v !== null && v !== "" && Number(v) > 0;
  }).length;
  const totalFilled = filledRequired + Math.min(filledOptional, 3);
  const progress = Math.round((totalFilled / (requiredFields.length + 3)) * 100);
  const filteredCategories = useMemo(
    () => (categories ?? []).filter(category => category.name.toLocaleLowerCase().includes(categorySearch.trim().toLocaleLowerCase())),
    [categories, categorySearch],
  );

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    setSavingCategory(true);
    try {
      const created = await createCategory.mutateAsync({ data: { name } });
      queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
      form.setValue("categoryId", String(created.id), { shouldValidate: true });
      setNewCategoryName("");
      setShowNewCategory(false);
      toast({ title: `Category "${name}" created`, description: "It has been selected for this product." });
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      const status = (error as { status?: number; response?: { status?: number } } | null)?.status
        ?? (error as { response?: { status?: number } } | null)?.response?.status;
      if (status === 409 || /already exists/i.test(message)) {
        await queryClient.invalidateQueries({ queryKey: getListCategoriesQueryKey() });
        setNewCategoryName("");
        setShowNewCategory(false);
        setCategoryOpen(true);
        toast({
          title: "Category already exists",
          description: "Choose the saved category from the list, or search for its name.",
        });
      } else {
        toast({ title: "Failed to create category", description: "Please try again.", variant: "destructive" });
      }
    } finally {
      setSavingCategory(false);
    }
  };

  // Debounced SKU check — fires 600ms after the user stops typing
  const watchedSku = useWatch({ control: form.control, name: "sku" });
  useEffect(() => {
    if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current);
    skuDebounceRef.current = setTimeout(() => checkSku(watchedSku ?? ""), 600);
    return () => { if (skuDebounceRef.current) clearTimeout(skuDebounceRef.current); };
  }, [watchedSku, checkSku]);

  const doSubmit = (data: ProductFormValues, again: boolean, shouldPrint = false, submittedVariants = variants) => {
    const extra: Record<string, unknown> = { isService: isServiceType };
    if (isVariableType) {
      extra.hasVariants = true;
      extra.variants = JSON.stringify(submittedVariants.map(v => ({
        id: v.id,
        label: [v.color.trim(), v.size.trim()].filter(Boolean).join(" / "),
        sku: v.sku,
        price: Number(v.price) || 0,
        stock: Number(v.stock) || 0,
        ...(v.color.trim() && v.size.trim() ? {
          attribute1Label: "Color",
          attribute1Value: v.color.trim(),
          attribute2Label: "Size",
          attribute2Value: v.size.trim(),
        } : {}),
      })));
    } else {
      extra.hasVariants = false;
    }
    onSubmit({ ...data, ...extra } as ProductFormValues, again, shouldPrint);
    if (again) {
      form.reset(PRODUCT_FORM_DEFAULTS);
      setMargin("");
      setImagePreview(null);
      setProductType("single");
    }
  };

  const handleSubmit = async (data: ProductFormValues) => {
    if (productSubmitLockRef.current) return;
    productSubmitLockRef.current = true;

    if (!String(data.sku ?? "").trim()) {
      const generatedSku = await generateSKU();
      if (!generatedSku) {
        productSubmitLockRef.current = false;
        return;
      }
      data = { ...data, sku: generatedSku };
    }
    const verifiedConflict = await checkSku(data.sku ?? "");
    const unchangedExistingSku = productId &&
      data.sku?.trim().toLowerCase() === defaultValues?.sku?.trim().toLowerCase();
    if (verifiedConflict && !unchangedExistingSku) {
      toast({
        title: "Barcode already in use",
        description: `Change the SKU used by "${verifiedConflict.name}"${verifiedConflict.variantLabel ? ` (${verifiedConflict.variantLabel})` : ""}, or generate a unique code for this product.`,
        variant: "destructive",
      });
      productSubmitLockRef.current = false;
      return;
    }
    if (isVariableType && !productId) {
      const used = new Set<string>();
      for (const [label, sku] of [["product", data.sku ?? ""], ...variants.map(v => [v.label || "variant", v.sku])]) {
        const code = sku.trim().toLowerCase();
        if (!code) continue;
        if (used.has(code)) {
          toast({ title: "Duplicate variant barcode", description: `Give ${label} a unique SKU before saving.`, variant: "destructive" });
          productSubmitLockRef.current = false;
          return;
        }
        used.add(code);
      }
    }

    let submittedVariants = variants;
    const variantsMissingSku = isVariableType ? variants.filter(variant => !variant.sku.trim()) : [];
    if (variantsMissingSku.length > 0) {
      setVariantSkusGenerating(true);
      try {
        const generated = await Promise.all(variantsMissingSku.map(async variant => {
          const response = await fetch("/api/products/generate-barcode", {
            method: "POST",
            credentials: "include",
          });
          const result = await response.json();
          if (!response.ok || typeof result.barcode !== "string" || !result.barcode.trim()) {
            throw new Error(result.error || "Could not generate a unique code.");
          }
          return [variant.id, result.barcode] as const;
        }));
        const barcodeByVariant = new Map(generated);
        submittedVariants = variants.map(variant => ({
          ...variant,
          sku: variant.sku.trim() || barcodeByVariant.get(variant.id) || "",
        }));
        setVariants(submittedVariants);
      } catch (error) {
        toast({
          title: "Variant barcodes not generated",
          description: error instanceof Error ? error.message : "Please try saving again.",
          variant: "destructive",
        });
        productSubmitLockRef.current = false;
        return;
      } finally {
        setVariantSkusGenerating(false);
      }
    }

    saveMutationStartedRef.current = true;
    doSubmit(data, addAnother, printAfterSave, submittedVariants);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col h-full">
        {/* Progress bar */}
        <div className="px-6 py-3 border-b bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>Form Progress</span>
            <span className="font-semibold text-foreground">{progress}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-8">
          {/* Tip banner */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <span className="text-xl flex-shrink-0">🛒</span>
            <div>
              <p className="text-sm font-semibold text-emerald-900">Stock your virtual shelves</p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Fill in the details exactly how you want your cashiers to see them. Fields marked <span className="text-destructive font-bold">*</span> are required — everything else is optional.
              </p>
            </div>
          </div>

          {/* Section 1 — Product Type */}
          {!isEdit && (
            <div className="space-y-4">
              <SectionHeader number={1} title="Product Type" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {PRODUCT_TYPES.map(({ value, label, desc, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setProductType(value)}
                    className={cn(
                      "flex flex-col items-start gap-1.5 rounded-xl border-2 p-3 text-left transition-all",
                      productType === value
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border hover:border-primary/40 hover:bg-muted/40"
                    )}
                  >
                    <Icon className={cn("w-4 h-4", productType === value ? "text-primary" : "text-muted-foreground")} />
                    <span className={cn("text-xs font-semibold leading-tight", productType === value ? "text-primary" : "text-foreground")}>{label}</span>
                    <span className="text-[10px] text-muted-foreground leading-tight">{desc}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Section 2 — Basic Details */}
          <div className="space-y-5">
            <SectionHeader number={isEdit ? 1 : 2} title="Basic Details" />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {/* Image upload */}
              <div className="sm:col-span-1">
                <label className="block text-sm font-medium mb-2">Product Image <span className="text-muted-foreground font-normal">(Optional)</span></label>
                <label className="flex flex-col items-center justify-center w-full aspect-square rounded-xl border-2 border-dashed border-border cursor-pointer hover:bg-muted/30 transition-colors overflow-hidden relative">
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} className="w-full h-full object-cover" alt="preview" />
                      <button
                        type="button"
                        onClick={e => { e.preventDefault(); setImagePreview(null); }}
                        className="absolute top-2 right-2 bg-background/80 rounded-full p-1 border"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </>
                  ) : (
                    <>
                      <ImagePlus className="w-8 h-8 text-muted-foreground mb-2" />
                      <span className="text-sm text-muted-foreground">Upload photo</span>
                    </>
                  )}
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                </label>
                <FieldHint>A clear photo helps cashiers identify items quickly at the POS.</FieldHint>
              </div>

              {/* Right fields */}
              <div className="sm:col-span-2 space-y-4">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="after:content-['*'] after:text-destructive after:ml-0.5">Product Name</FormLabel>
                    <FormControl><Input placeholder="e.g., Coca-Cola 50cl, Samsung Galaxy S24" {...field} /></FormControl>
                    <FieldHint>Use the full product name as it will appear on receipts and the POS screen.</FieldHint>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="categoryId" render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center justify-between">
                      <FormLabel className="after:content-['*'] after:text-destructive after:ml-0.5">Category</FormLabel>
                      <button
                        type="button"
                        onClick={() => { setShowNewCategory(v => !v); setNewCategoryName(""); }}
                        className="text-xs text-primary hover:underline flex items-center gap-1"
                      >
                        <Plus className="w-3 h-3" />
                        {showNewCategory ? "Cancel" : "New category"}
                      </button>
                    </div>
                    <div ref={categoryPickerRef} className="relative">
                      <FormControl>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-haspopup="listbox"
                          aria-expanded={categoryOpen}
                          aria-controls="product-category-options"
                          data-testid="button-select-product-category"
                          onClick={() => {
                            if (categoryOpen) {
                              closeCategoryPicker();
                            } else {
                              setCategoryOpen(true);
                            }
                          }}
                          className="w-full justify-between font-normal"
                        >
                          {categories?.find(category => String(category.id) === field.value)?.name
                            ?? (categoriesLoading ? "Loading categories…" : "Select a category")}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                      {categoryOpen && (
                        <div className="absolute left-0 right-0 top-full z-10 mt-1 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg">
                          <div className="border-b p-2">
                            <Input
                              aria-label="Search categories"
                              data-testid="input-search-product-category"
                              placeholder="Search categories…"
                              value={categorySearch}
                              onChange={event => setCategorySearch(event.target.value)}
                              className="h-9"
                            />
                          </div>
                          <div
                            id="product-category-options"
                            role="listbox"
                            aria-label="Saved categories"
                            className="max-h-60 overflow-y-auto p-1"
                          >
                            {categoriesLoading ? (
                              <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading categories…</p>
                            ) : filteredCategories.length ? (
                              filteredCategories.map(category => (
                                <button
                                  key={category.id}
                                  type="button"
                                  role="option"
                                  aria-selected={field.value === String(category.id)}
                                  onClick={() => {
                                    field.onChange(String(category.id));
                                    closeCategoryPicker();
                                  }}
                                  className="flex w-full items-center rounded-sm px-2 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:outline-none"
                                >
                                  <Check className={`mr-2 h-4 w-4 ${field.value === String(category.id) ? "opacity-100" : "opacity-0"}`} />
                                  {category.name}
                                </button>
                              ))
                            ) : (
                              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                                {categories?.length ? "No matching category." : "No saved categories yet — add one above."}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {showNewCategory && (
                      <div className="flex gap-2 mt-1">
                        <Input
                          autoFocus
                          placeholder="Category name, e.g. Beverages"
                          value={newCategoryName}
                          onChange={e => setNewCategoryName(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAddCategory(); } }}
                          className="h-8 text-sm"
                        />
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 gap-1 shrink-0"
                          disabled={!newCategoryName.trim() || savingCategory}
                          onClick={handleAddCategory}
                        >
                          {savingCategory ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                          Add
                        </Button>
                      </div>
                    )}
                    <FieldHint>Which aisle or group does this product belong to? (e.g., Drinks, Electronics)</FieldHint>
                    <FormMessage />
                  </FormItem>
                )} />

                <FormField control={form.control} name="brand" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Brand <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          list="brand-suggestions"
                          placeholder="e.g., Nike, Samsung, Dangote"
                        />
                        {brands.length > 0 && (
                          <datalist id="brand-suggestions">
                            {brands.map(b => <option key={b} value={b} />)}
                          </datalist>
                        )}
                      </div>
                    </FormControl>
                    <FieldHint>Type an existing brand to select it, or type any new brand name — it will be saved automatically when you save the product.</FieldHint>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            {/* SKU row */}
            <FormField control={form.control} name="sku" render={({ field }) => (
              <FormItem>
                <FormLabel>Barcode / Item Code (SKU) <span className="text-muted-foreground font-normal">(Optional — generated if blank)</span></FormLabel>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <FormControl><Input placeholder="Scan or type the item's existing barcode..." {...field} onChange={event => { field.onChange(event); setGeneratedSku(""); }} className="min-w-0 font-mono" /></FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-1.5 sm:w-auto sm:shrink-0"
                    onClick={generateSKU}
                    disabled={skuGenerating}
                  >
                    {skuGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Generate store code
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full gap-1.5 sm:w-auto sm:shrink-0"
                     onClick={() => {
                       const product = {
                         id: productId ?? -1,
                         name: String(watchedValues.name ?? "").trim() || "New product",
                         sku: String(watchedValues.sku ?? "").trim(),
                         price: Number(watchedValues.price ?? 0),
                       };
                       if (onPrintBarcode) onPrintBarcode(product);
                       else setBarcodePrinterOpen(true);
                     }}
                    disabled={!productId || !String(watchedValues.name ?? "").trim() || !String(watchedValues.sku ?? "").trim()}
                  >
                    <Printer className="w-3.5 h-3.5" /> {isVariableType ? "Print saved variants" : "Print barcode"}
                  </Button>
                </div>
                {String(watchedValues.sku ?? "").trim() && (
                  <ProductBarcodePreview
                    value={String(watchedValues.sku).trim()}
                    generated={generatedSku === String(watchedValues.sku).trim()}
                  />
                )}
                <FieldHint>{isVariableType
                  ? "Save the product first to print a separate barcode or price label for each variant."
                  : "Save first to print a label. Use the item's existing barcode, or leave blank to generate a unique store code when you save."}</FieldHint>
                {/* SKU conflict warning */}
                {skuChecking && (
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground mt-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Checking barcode…
                  </p>
                )}
                {!skuChecking && skuConflict && (
                  <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-900">Barcode already in use</p>
                      <p className="text-xs text-amber-800 mt-0.5">
                         <span className="font-medium">"{skuConflict.name}"{skuConflict.variantLabel ? ` (${skuConflict.variantLabel})` : ""}</span> already uses this barcode.
                         {productId && watchedSku?.trim().toLowerCase() === defaultValues?.sku?.trim().toLowerCase()
                           ? "This older shared code can stay while you edit other details, but change it before printing."
                           : "Change this SKU or generate a new one before saving."}
                      </p>
                       <div className="flex gap-2 mt-2">
                        <button
                          type="button"
                          onClick={generateSKU}
                          className="text-xs font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
                        >
                          Generate a new unique code instead
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <FieldHint>Scan the physical barcode with your scanner, or click Generate to auto-create a unique tracking code.</FieldHint>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                <FormControl><Textarea placeholder="Brief description of this product..." rows={2} {...field} /></FormControl>
                <FieldHint>Extra details visible to staff — not shown on receipts.</FieldHint>
                <FormMessage />
              </FormItem>
            )} />
          </div>

          {/* Section 3 — Units & Measurements */}
          <div className="space-y-4">
            <SectionHeader number={isEdit ? 2 : 3} title="Units & Measurements" />
            <FormField control={form.control} name="unit" render={({ field }) => (
              <FormItem>
                <FormLabel>Unit of Measurement</FormLabel>
                <div className="flex gap-2">
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl><SelectTrigger className="flex-1"><SelectValue placeholder="Select unit" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {UNITS.map(u => <SelectItem key={u} value={u}>{u.charAt(0).toUpperCase() + u.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Custom unit..."
                    className="flex-1"
                    value={field.value ?? ""}
                    onChange={e => field.onChange(e.target.value)}
                  />
                </div>
                <FieldHint>How is this item usually sold? (e.g., Piece, Carton, Litre, Kg)</FieldHint>
                <FormMessage />
              </FormItem>
            )} />
          </div>

          {/* Section 4 — Inventory */}
          <div className="space-y-4">
            <SectionHeader number={isEdit ? 3 : 4} title="Inventory" />
            {isEdit && (
              <button
                type="button"
                onClick={() => setProductType(p => p === "service" ? "single" : "service")}
                className={cn(
                  "w-full flex items-center justify-between rounded-xl border-2 px-4 py-3 text-left transition-all",
                  isServiceType
                    ? "border-violet-400 bg-violet-50"
                    : "border-border hover:border-primary/40 hover:bg-muted/30"
                )}
              >
                <div className="flex items-center gap-3">
                  <Wrench className={cn("w-4 h-4 shrink-0", isServiceType ? "text-violet-600" : "text-muted-foreground")} />
                  <div>
                    <p className={cn("text-sm font-semibold", isServiceType ? "text-violet-900" : "text-foreground")}>
                      {isServiceType ? "Service item — no stock tracking" : "Physical product — stock tracked"}
                    </p>
                    <p className={cn("text-xs mt-0.5", isServiceType ? "text-violet-700" : "text-muted-foreground")}>
                      {isServiceType
                        ? "Can always be sold at the POS regardless of stock level."
                        : "Click to mark as a service (e.g. haircut, repair, consultation)."}
                    </p>
                  </div>
                </div>
                <div className={cn(
                  "w-10 h-6 rounded-full flex items-center transition-colors flex-shrink-0",
                  isServiceType ? "bg-violet-500 justify-end" : "bg-muted justify-start"
                )}>
                  <div className="w-5 h-5 rounded-full bg-white shadow mx-0.5" />
                </div>
              </button>
            )}
            {isServiceType ? (
              <div className="rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-3">
                <Wrench className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-violet-900">No stock tracking for services</p>
                  <p className="text-xs text-violet-700 mt-0.5">
                    Service items (e.g. haircut, consultation, repair, laundry) are never marked out of stock and won't trigger low-stock alerts. Stock will not be deducted when sold at the POS.
                  </p>
                </div>
              </div>
            ) : isMaterialType ? (
              <>
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3">
                  <Wheat className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-amber-900">Raw Material — stock is tracked</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Ingredients and production inputs. Stock is deducted as you use them. Set a reorder level so you get alerted before you run out.
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField control={form.control} name="stock" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="after:content-['*'] after:text-destructive after:ml-0.5">Quantity in Stock</FormLabel>
                      <FormControl><Input type="number" min={0} step="0.001" placeholder="e.g., 50" {...field} /></FormControl>
                      <FieldHint>How much of this material do you currently have?</FieldHint>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="reorderLevel" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reorder Alert Level</FormLabel>
                      <FormControl><Input type="number" min={0} placeholder="e.g., 5" {...field} /></FormControl>
                      <FieldHint>You'll be alerted when stock drops to this quantity.</FieldHint>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              </>
            ) : isVariableType ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold">Product Variants</p>
                    <p className="text-xs text-muted-foreground">Each variant has its own price and stock level.</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={addVariantRow} className="gap-1"><Plus className="w-3 h-3" /> Add Variant</Button>
                </div>
                {variants.length === 0 && (
                  <div className="text-center py-4 border border-dashed rounded-lg text-muted-foreground text-xs">No variants yet. Click "Add Variant" to start.</div>
                )}
                {variants.map(v => (
                  <div key={v.id} className="rounded-lg border p-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Input aria-label="Variant color" placeholder="Color (e.g. Blue)" value={v.color} onChange={e => updateVariantRow(v.id, "color", e.target.value)} className="h-9 text-sm" />
                      <Input aria-label="Variant size" placeholder="Size (e.g. L or 4T)" value={v.size} onChange={e => updateVariantRow(v.id, "size", e.target.value)} className="h-9 text-sm" />
                    </div>
                    <div className="grid grid-cols-[minmax(0,1fr)_72px_72px_32px] gap-2 items-center">
                      <Input aria-label="Variant barcode or SKU" placeholder="Barcode / SKU (auto-generated if blank)" value={v.sku} onChange={e => updateVariantRow(v.id, "sku", e.target.value)} className="h-9 text-xs min-w-0" />
                      <Input aria-label="Variant price" placeholder="Price ₦" type="number" min="0" value={v.price} onChange={e => updateVariantRow(v.id, "price", e.target.value)} className="h-9 text-xs" />
                      <Input aria-label="Variant stock" placeholder="Stock" type="number" min="0" value={v.stock} onChange={e => updateVariantRow(v.id, "stock", e.target.value)} className="h-9 text-xs" />
                      <button type="button" aria-label="Remove variant" onClick={() => removeVariantRow(v.id)} className="text-muted-foreground hover:text-destructive"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ))}
                {variants.length > 0 && <p className="text-xs text-muted-foreground">{variantSkusGenerating ? "Generating unique barcode for each variant without one…" : "Each variant gets its own barcode. A unique store code is generated when its barcode field is blank."}</p>}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField control={form.control} name="stock" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="after:content-['*'] after:text-destructive after:ml-0.5">Current Stock on Hand</FormLabel>
                    <FormControl><Input type="number" min={0} step="0.001" placeholder="e.g., 50" {...field} /></FormControl>
                    <FieldHint>How many units do you have right now? Enter 0 if none. Decimals allowed (e.g. 2.5 kg).</FieldHint>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="reorderLevel" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Low Stock Alert Quantity</FormLabel>
                    <FormControl><Input type="number" min={0} placeholder="e.g., 5" {...field} /></FormControl>
                    <FieldHint>RetailPulse will flag this item as low stock when it drops to this number.</FieldHint>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            )}

            {/* Expiry date */}
            <FormField
              control={form.control}
              name="expiryDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Expiry Date <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ""} onChange={e => field.onChange(e.target.value || null)} />
                  </FormControl>
                  <FieldHint>For perishables, medicines, or any dated goods. Items within 30 days of expiry will be flagged in your inventory.</FieldHint>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="rounded-xl border p-4">
            <FormField control={form.control} name="vatApplicable" render={({ field }) => (
              <FormItem className="flex items-start gap-3 space-y-0">
                <FormControl>
                  <Checkbox checked={field.value ?? false} onCheckedChange={value => field.onChange(value === true)} />
                </FormControl>
                <div className="space-y-1">
                  <FormLabel>Nigeria VAT applies to this product</FormLabel>
                  <FieldHint>
                    Leave unticked unless you have confirmed this item is taxable under Nigeria's Tax Act and NRS guidance. The VAT rate is set in store settings; products are exempt by default.
                  </FieldHint>
                  <FormMessage />
                </div>
              </FormItem>
            )} />
          </div>

          {/* Section 5 — Pricing */}
          <div className="space-y-4">
            <SectionHeader number={isEdit ? 4 : 5} title="Pricing" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField control={form.control} name="costPrice" render={({ field }) => (
                <FormItem>
                  <FormLabel>{isMaterialType ? "Cost / Purchase Price per Unit" : "Purchase / Cost Price"}</FormLabel>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">₦</span>
                    <FormControl><Input type="number" step="0.01" min={0} placeholder="0.00" className="pl-7" {...field} /></FormControl>
                  </div>
                  <FieldHint>{isMaterialType ? "How much does one unit of this material cost you?" : "How much did you pay/buy this item for? Used to calculate profit."}</FieldHint>
                  <FormMessage />
                </FormItem>
              )} />

              {/* Profit margin helper */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Desired Profit Margin <span className="text-muted-foreground font-normal">(Optional)</span></label>
                <div className="flex gap-2 items-center">
                  <div className="relative flex-1">
                    <Input
                      type="number"
                      min={0}
                      placeholder="e.g., 30"
                      value={margin}
                      onChange={e => setMargin(e.target.value)}
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={applyMargin} disabled={!costPrice || !margin}>
                    Apply
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Enter a margin % and click Apply to auto-fill the selling price.
                  {computedMargin !== null && <span className="text-emerald-600 font-medium ml-1">Current margin: {computedMargin}%</span>}
                </p>
              </div>
            </div>

            <FormField control={form.control} name="price" render={({ field }) => (
              <FormItem>
                <FormLabel className={isMaterialType ? undefined : "after:content-['*'] after:text-destructive after:ml-0.5"}>
                  {isMaterialType ? "Internal / Transfer Price (Optional)" : "Selling Price (What customers pay)"}
                </FormLabel>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-muted-foreground">₦</span>
                  <FormControl><Input type="number" step="0.01" min={0} placeholder="0.00" className="pl-7 text-lg font-semibold" {...field} /></FormControl>
                </div>
                <FieldHint>
                  {isMaterialType
                    ? "Only needed if this material is sometimes sold directly. Leave as 0 if it's only used internally."
                    : "This is the price charged at the POS and shown on all receipts."}
                </FieldHint>
                <FormMessage />
              </FormItem>
            )} />

            {costPrice > 0 && sellingPrice > 0 && (
              <div className={cn(
                "rounded-xl px-4 py-3 flex items-center justify-between text-sm",
                sellingPrice > costPrice ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"
              )}>
                <span className={sellingPrice > costPrice ? "text-emerald-700" : "text-red-700"}>
                  {sellingPrice > costPrice ? "✅ Profit per unit:" : "⚠️ Selling below cost:"}
                </span>
                <span className={cn("font-bold", sellingPrice > costPrice ? "text-emerald-800" : "text-red-800")}>
                  ₦{Math.abs(sellingPrice - costPrice).toLocaleString("en-NG", { minimumFractionDigits: 2 })}
                  {computedMargin !== null && <span className="font-normal text-xs ml-2">({computedMargin}% margin)</span>}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Sticky footer */}
        <div className="flex-shrink-0 border-t px-6 py-4 flex items-center justify-between gap-3 bg-background">
          <Button type="button" variant="ghost" onClick={onCancel} className="gap-1.5">
            <X className="w-4 h-4" /> Cancel
          </Button>
          <div className="flex flex-wrap justify-end gap-2">
            {!isEdit && (
              <Button
                type="submit"
                variant="outline"
                disabled={isPending || form.formState.isSubmitting || skuGenerating || variantSkusGenerating}
                onClick={() => { setAddAnother(true); setPrintAfterSave(false); }}
                className="gap-2"
              >
                {isPending && addAnother ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                + Save & Add Another
              </Button>
            )}
            {!isEdit && (
              <Button
                type="submit"
                variant="secondary"
                disabled={isPending || form.formState.isSubmitting || skuGenerating || variantSkusGenerating}
                className="gap-2"
                onClick={() => { setAddAnother(false); setPrintAfterSave(true); }}
              >
                {isPending && printAfterSave ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />}
                {isVariableType ? "Save & Print Variant Labels" : "Save & Print Barcode"}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isPending || form.formState.isSubmitting || skuGenerating || variantSkusGenerating}
              className="gap-2 min-w-[130px]"
              onClick={() => { setAddAnother(false); setPrintAfterSave(false); }}
            >
              {isPending && !addAnother ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEdit ? "Save Changes" : "Save Product"}
            </Button>
          </div>
        </div>
      </form>

      {!onPrintBarcode && store?.id != null && (
        <BarcodePrinter
          open={barcodePrinterOpen}
          onClose={() => setBarcodePrinterOpen(false)}
          products={[{
            id: productId ?? -1,
            name: String(defaultValues?.name ?? "").trim() || "Saved product",
            sku: String(defaultValues?.sku ?? "").trim(),
            price: Number(defaultValues?.price ?? 0),
          }]}
          storeId={store.id}
          storeName={store?.name ?? ""}
          autoSelectAll
        />
      )}
    </Form>
  );
}
